/**
 * R6 — the weapon-range ring: `targeting.visible_range_u`, drawn.
 *
 * **Why it exists.** `src/sim/targeting.ts` clamps every acquisition path to a circle of
 * `targeting.visible_range_u` around the ship, so shots never leave the muzzle toward a
 * body the player cannot see. That clamp is invisible: from the cockpit it looks like the
 * guns are ignoring a target for no stated reason. The Tower's answer to the same problem
 * is to draw the radius, which turns a hidden rule into an honest one — you learn the
 * engagement envelope by looking at it once, and then you stop looking.
 *
 * **The treatment is restraint.** This is on for the whole run, so it is built to be read
 * peripherally and then forgotten: a 1.5 px core with a short outward skirt, alpha 0.20,
 * in `pal.ui.range` — the sonar-instrument family, one step off the major grid line.
 * Three things it must not do, all of them constraints with a prior:
 *  - AMD-7 R3: the player is the brightest pixel in frame (1.000 against 0.945–0.972 for
 *    everything else). A ring at 0.20 alpha over the abyssal floor peaks around 0.09
 *    luminance, well under the 0.32 bloom threshold, so it never even reaches the bloom
 *    pass and cannot move that measurement.
 *  - §11.9 reserves magenta for hazards and the attacker's own hue for telegraphs. A
 *    desaturated sonar blue is in neither vocabulary, so the ring cannot be misread as
 *    something about to hurt you.
 *  - §11.10 density dimming does not apply: the ring is already at the floor of what is
 *    visible, and dimming it further would make it flicker in and out during a surge.
 * It draws under every entity (`LAYER_RANGE_RING`, above the parallax field and below the
 * hazard layer), so nothing alive is ever occluded by it.
 *
 * **The geometry is built on the CPU, per vertex, and that is the whole point (AMD-24).**
 * The instanced path warps the instance ORIGIN only — the seam wrap has to move a whole
 * instance or the quad tears, and the curvature has to leave a silhouette alone or a
 * telegraph stops being the shape §11.9 measured. For a body that is exactly right. For
 * this ring it is exactly wrong, and the reason is arithmetic:
 *
 *   at `render.curvature` 0.16 and the reference half-diagonal of 30.6 u, a point 30 u
 *   from the focus is drawn at `30 / (1 + 0.16 · (30/30.6)²) = 26.0 u`.
 *
 * An enemy sitting on the acquisition boundary is therefore drawn 4 u inside where an
 * origin-warped ring instance would put its rim — about 145 px at 1080p. The ring would
 * be a picture of a rule that visibly disagrees with the bodies it governs, which is
 * worse than not drawing it. So: sample the circle at `SEGMENTS` points, push each sample
 * through `warpPoint` (the JS mirror of `warpWorld`, so the CPU and the GPU agree by
 * construction), and hand the shader coordinates that are already resolved.
 *
 * That choice buys the seam for free as well. `warpPoint` starts from `wrapDeltaX` /
 * `wrapDeltaY`, the short arc on the torus, so a ship at x = 98 gets samples that run off
 * the right edge and come back on the left as one continuous band. A single ring instance
 * could not do that at all: its origin would resolve to one image of the torus and the
 * half of the circle past the cut would simply be drawn 200 u away, off frame.
 *
 * Costs: one draw call, one 96-segment band, one preallocated buffer, no allocation after
 * boot, and the whole rebuild is skipped when `render.range_ring` is false.
 */

import * as THREE from 'three';
import { LAYER_RANGE_RING } from './instanced';
import { LINEAR, tokenIndex } from './palette';
import { TRAIL_FRAG, TRAIL_VERT } from './shaders/instanced';
import { WARP_OUT, screenWarpUniforms, warpPoint } from './warp';

const TOK_RANGE = tokenIndex('pal.ui.range');

/**
 * Samples around the circle. At 96 the chord error on a 30 u ring is 30·(1−cos(π/96)) =
 * 0.016 u — half a pixel at the reference framing — so the band reads as a curve and not
 * as a polygon even when the curvature is bowing it.
 */
const SEGMENTS = 96;
/**
 * Band profile, in reference pixels (36 px per world unit at the §11 framing).
 *
 * A 1.5 px core with a 3 px outward skirt. The skirt sits OUTSIDE the core rather than
 * straddling it so the ring's inner edge stays the crisp one: the inner edge is the
 * boundary that means something (inside it, the guns engage), and a symmetric feather
 * would smear the very line the player is reading.
 */
const CORE_PX = 1.5;
const SKIRT_PX = 3;
const U_PER_PX = 1 / 36;
const CORE_HALF = (CORE_PX * 0.5) * U_PER_PX;
const SKIRT = SKIRT_PX * U_PER_PX;

/** Peak alpha on the core. §11.10 does not dim this; it is already at the floor. */
const CORE_ALPHA = 0.2;
/** The skirt exists to keep the ring from aliasing into a dotted line under the CRT. */
const SKIRT_ALPHA = 0.055;

/**
 * Two triangles per segment for the core band and two for the skirt: 12 vertices per
 * segment, closed, so the strip needs no seam stitching.
 */
const VERTS_PER_SEGMENT = 12;
const MAX_VERTS = SEGMENTS * VERTS_PER_SEGMENT;

export class RangeRing {
  readonly mesh: THREE.Mesh;

  private readonly geometry = new THREE.BufferGeometry();
  private readonly material: THREE.RawShaderMaterial;
  private readonly positions = new Float32Array(MAX_VERTS * 3);
  private readonly colors = new Float32Array(MAX_VERTS * 4);
  private readonly posAttr: THREE.BufferAttribute;
  private readonly colAttr: THREE.BufferAttribute;

  /** Precomputed unit circle. The radius is a config value and moves; the angles do not. */
  private readonly cosT = new Float32Array(SEGMENTS + 1);
  private readonly sinT = new Float32Array(SEGMENTS + 1);

  /**
   * Warped samples for this frame, kept between the two passes so the skirt band reuses
   * the ring the core band already computed instead of running `warpOffset` twice.
   */
  private readonly sx = new Float32Array(SEGMENTS + 1);
  private readonly sy = new Float32Array(SEGMENTS + 1);
  /** Outward unit normal at each sample, in warped space. */
  private readonly nx = new Float32Array(SEGMENTS + 1);
  private readonly ny = new Float32Array(SEGMENTS + 1);

  private vertexCount = 0;

  constructor(scene: THREE.Scene) {
    for (let i = 0; i <= SEGMENTS; i++) {
      const a = (i / SEGMENTS) * Math.PI * 2;
      this.cosT[i] = Math.cos(a);
      this.sinT[i] = Math.sin(a);
    }

    this.posAttr = new THREE.BufferAttribute(this.positions, 3);
    this.colAttr = new THREE.BufferAttribute(this.colors, 4);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.colAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('position', this.posAttr);
    this.geometry.setAttribute('color', this.colAttr);
    this.geometry.setDrawRange(0, 0);
    // Never culled: the band is already resolved to the camera's image of the torus, and
    // a bounding sphere computed from those coordinates would thrash across the seam.
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1e6);

    this.material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: TRAIL_VERT,
      fragmentShader: TRAIL_FRAG,
      // Identity: the CPU has already applied both halves of `warpWorld`, and applying
      // either one a second time in the vertex program would bow an already bowed ring.
      uniforms: screenWarpUniforms(),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      // Normal blending, not the additive the trails use. Additive on a persistent
      // full-screen-width element is exactly what AMD-6 caps at 0.06 background
      // luminance; alpha blending puts a hard ceiling on the ring's contribution and
      // keeps it from accumulating where it crosses a bright body's bloom.
      blending: THREE.NormalBlending,
      side: THREE.DoubleSide,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.renderOrder = LAYER_RANGE_RING;
    this.mesh.visible = false;
    this.mesh.name = 'range-ring';
    scene.add(this.mesh);
  }

  /**
   * Rebuilds the band around `(cx, cy)` at world radius `radius`.
   *
   * Call after `setWarp` for the frame — `warpPoint` reads the transform this frame
   * published, so a rebuild before it would draw last frame's curvature.
   */
  build(cx: number, cy: number, radius: number, enabled: boolean): void {
    if (!enabled || radius <= 0.01) {
      this.vertexCount = 0;
      this.geometry.setDrawRange(0, 0);
      this.mesh.visible = false;
      return;
    }

    // Pass 1: warp every sample. `warpPoint` is the JS mirror of the shader's
    // `warpWorld` and returns ABSOLUTE world space on the camera's image of the torus,
    // which is the space the vertex program's modelView matrix expects.
    for (let i = 0; i <= SEGMENTS; i++) {
      warpPoint(cx + this.cosT[i] * radius, cy + this.sinT[i] * radius);
      this.sx[i] = WARP_OUT.x;
      this.sy[i] = WARP_OUT.y;
    }
    // Pass 2: the outward normal, taken from the WARPED neighbours rather than from the
    // authored angle. The curvature is a non-uniform contraction, so the warped ring is
    // an oval near the frame rim and the authored radial direction is no longer its
    // normal; using it would give the band a varying apparent width around the circle.
    for (let i = 0; i <= SEGMENTS; i++) {
      const p = i === 0 ? SEGMENTS - 1 : i - 1;
      const n = i === SEGMENTS ? 1 : i + 1;
      let tx = this.sx[n] - this.sx[p];
      let ty = this.sy[n] - this.sy[p];
      const len = Math.hypot(tx, ty);
      if (len < 1e-6) {
        // Degenerate only if the curvature has collapsed two samples onto each other.
        // Fall back to the authored radial, which is right to first order.
        tx = this.cosT[i];
        ty = this.sinT[i];
      } else {
        tx /= len;
        ty /= len;
      }
      // Rotate the tangent by −90° to get the outward normal for a CCW ring.
      this.nx[i] = len < 1e-6 ? tx : ty;
      this.ny[i] = len < 1e-6 ? ty : -tx;
    }

    const cr = LINEAR[TOK_RANGE * 3];
    const cg = LINEAR[TOK_RANGE * 3 + 1];
    const cb = LINEAR[TOK_RANGE * 3 + 2];

    let v = 0;
    for (let i = 0; i < SEGMENTS; i++) {
      const j = i + 1;
      const ax = this.sx[i];
      const ay = this.sy[i];
      const bx = this.sx[j];
      const by = this.sy[j];
      const anx = this.nx[i];
      const any = this.ny[i];
      const bnx = this.nx[j];
      const bny = this.ny[j];

      // Core: a band of CORE_HALF either side of the sampled curve.
      v = this.quad(
        v,
        ax - anx * CORE_HALF, ay - any * CORE_HALF, CORE_ALPHA,
        ax + anx * CORE_HALF, ay + any * CORE_HALF, CORE_ALPHA,
        bx + bnx * CORE_HALF, by + bny * CORE_HALF, CORE_ALPHA,
        bx - bnx * CORE_HALF, by - bny * CORE_HALF, CORE_ALPHA,
        cr, cg, cb,
      );
      // Skirt: outward only, fading to nothing, so the inner edge stays the crisp one.
      v = this.quad(
        v,
        ax + anx * CORE_HALF, ay + any * CORE_HALF, SKIRT_ALPHA,
        ax + anx * (CORE_HALF + SKIRT), ay + any * (CORE_HALF + SKIRT), 0,
        bx + bnx * (CORE_HALF + SKIRT), by + bny * (CORE_HALF + SKIRT), 0,
        bx + bnx * CORE_HALF, by + bny * CORE_HALF, SKIRT_ALPHA,
        cr, cg, cb,
      );
    }

    this.vertexCount = v;
    this.geometry.setDrawRange(0, v);
    this.mesh.visible = v > 0;
    this.posAttr.clearUpdateRanges();
    this.posAttr.addUpdateRange(0, v * 3);
    this.posAttr.needsUpdate = true;
    this.colAttr.clearUpdateRanges();
    this.colAttr.addUpdateRange(0, v * 4);
    this.colAttr.needsUpdate = true;
  }

  private quad(
    v: number,
    ax: number, ay: number, aa: number,
    bx: number, by: number, ba: number,
    cx: number, cy: number, ca: number,
    dx: number, dy: number, da: number,
    r: number, g: number, b: number,
  ): number {
    let n = v;
    n = this.emit(n, ax, ay, r, g, b, aa);
    n = this.emit(n, bx, by, r, g, b, ba);
    n = this.emit(n, cx, cy, r, g, b, ca);
    n = this.emit(n, ax, ay, r, g, b, aa);
    n = this.emit(n, cx, cy, r, g, b, ca);
    n = this.emit(n, dx, dy, r, g, b, da);
    return n;
  }

  private emit(v: number, x: number, y: number, r: number, g: number, b: number, a: number): number {
    this.positions[v * 3] = x;
    this.positions[v * 3 + 1] = y;
    this.positions[v * 3 + 2] = 0;
    this.colors[v * 4] = r;
    this.colors[v * 4 + 1] = g;
    this.colors[v * 4 + 2] = b;
    this.colors[v * 4 + 3] = a;
    return v + 1;
  }

  get vertices(): number {
    return this.vertexCount;
  }

  clear(): void {
    this.vertexCount = 0;
    this.geometry.setDrawRange(0, 0);
    this.mesh.visible = false;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
